import { AdapterRegistry } from '../adapter-registry';
import { Metamodel } from 'app/model/base/model';
import { AzureDatabricksDeltaLakeLinkedService } from 'app/model/linkedservice/azure-databricks-deltalake.linkedservice';
import { LinkedServiceAdapter } from './linkedservice.adapter';
import { IModel } from 'app/model/base/annotation';
import { Model } from 'app/model/backendmodel';
import { parameterizablePasswordOrAKVtoSecretBase, parameterizableSecretBaseToModel } from './azure-key-vault.util';
import { AuthenticationType, ReferenceAdapter, Credential, CredentialTypeOptions, ITridentResource } from 'shell-models-lib';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
@AdapterRegistry.adapter({
    model: Metamodel.modelFor(AzureDatabricksDeltaLakeLinkedService),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            connectVia: '*',
            typeProperties: {
                domain: '*',
                clusterId: '*',
                accessToken: 'secret',
                encryptedCredential: '*',
                credential: '*',
                workspaceResourceId: '*'
            }
        },
        parameters: '*'
    }
})
export class AzureDatabricksDeltaLakeLinkedServiceAdapter extends LinkedServiceAdapter<AzureDatabricksDeltaLakeLinkedService> {
    protected supportFirstClassParameterExperience = true;
    /**
     * Update AuthenticationType based on fetched credential type
     * @param model linked service model
     */
    public async postToModel(model: AzureDatabricksDeltaLakeLinkedService): Promise<void> {
        let finalAuthType: AuthenticationType = model.authenticationMethod;
        if (model.credential) {
            const credType = model.credential.getType();

            if (credType === CredentialTypeOptions.UserAuth) {
                finalAuthType = AuthenticationType.UserAuth;
            } else if (credType === CredentialTypeOptions.UserAssignedManagedIdentity) {
                finalAuthType = AuthenticationType.UserAssignedManagedIdentity;
            }
        }
        model.authenticationMethod = finalAuthType;
    }

    protected modelConstructor(): new () => AzureDatabricksDeltaLakeLinkedService {
        return AzureDatabricksDeltaLakeLinkedService;
    }

    protected model(): IModel {
        return AzureDatabricksDeltaLakeLinkedService.MODEL;
    }

    protected async propertiesToResourceAsync(linkedService: AzureDatabricksDeltaLakeLinkedService, linkedServiceResource: Model.LinkedServiceResource): Promise<Model.LinkedServiceResource> {
        let credential;
        let workspaceResourceId;
        let accessToken;

        const properties = this.properties(linkedServiceResource) as Model.AzureDatabricksDeltaLakeLinkedService;
        properties.type = this.model().name;
        if ((linkedService.authenticationMethod === AuthenticationType.UserAssignedManagedIdentity || linkedService.authenticationMethod === AuthenticationType.UserAuth) && linkedService.credential) {
            credential = ReferenceAdapter.toResource(linkedService.credential, this.context) as Model.CredentialReference;
        }
        if (linkedService.authenticationMethod === AuthenticationType.AccessToken) {
            accessToken = parameterizablePasswordOrAKVtoSecretBase(linkedService, AzureDatabricksDeltaLakeLinkedService.ACCESS_TOKEN, this.context);
        } else { // MSI & UAMI & UserAuth
            workspaceResourceId = this.valueToResource(linkedService, AzureDatabricksDeltaLakeLinkedService.WORKSPACE_RESOURCE_ID);
        }

        properties.typeProperties = {
            domain: this.valueToResource(linkedService, AzureDatabricksDeltaLakeLinkedService.DOMAIN),
            clusterId: this.valueToResource(linkedService, AzureDatabricksDeltaLakeLinkedService.CLUSTER_ID),
            accessToken,
            credential,
            workspaceResourceId
        };
        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(linkedServiceResource: Model.LinkedServiceResource, linkedService: AzureDatabricksDeltaLakeLinkedService): Promise<AzureDatabricksDeltaLakeLinkedService> {
        const properties = linkedServiceResource.properties as Model.AzureDatabricksDeltaLakeLinkedService;
        const typeProperties = properties.typeProperties;

        ValueAdapter.toModel(linkedService, AzureDatabricksDeltaLakeLinkedService.DOMAIN, typeProperties.domain);
        ValueAdapter.toModel(linkedService, AzureDatabricksDeltaLakeLinkedService.CLUSTER_ID, typeProperties.clusterId);

        linkedService.manualSelected = Boolean(linkedService.clusterId) || ValueAdapter.isTemplated(linkedService, AzureDatabricksDeltaLakeLinkedService.CLUSTER_ID);
        linkedService.credential = undefined;
        if (typeProperties.workspaceResourceId) {
            ValueAdapter.toModel(linkedService, AzureDatabricksDeltaLakeLinkedService.WORKSPACE_RESOURCE_ID, typeProperties.workspaceResourceId);
            if (typeProperties.credential) {
                // let postToModel() to determine authenticationMethod
                linkedService.credential = ReferenceAdapter.toProxyModel(Metamodel.modelFor(Credential), typeProperties.credential, this.context);
            } else {
                linkedService.authenticationMethod = AuthenticationType.ManagedServiceIdentity;
            }
        } else {
            parameterizableSecretBaseToModel(linkedService, AzureDatabricksDeltaLakeLinkedService.ACCESS_TOKEN, typeProperties.accessToken, this.context);
            linkedService.authenticationMethod = AuthenticationType.AccessToken;
        }
        return linkedService;
    }

    protected override toAzureTypeProperties = (
        tridentResource: ITridentResource
    ): Partial<Model.AzureDatabricksDeltaLakeLinkedService> => {
        try {
            const parsedConnection = JSON.parse(tridentResource.connectionDetails);
            const { host, httpPath } = JSON.parse(parsedConnection.extensionDataSourcePath);
            return { domain: host, clusterId: httpPath };
        } catch (error) {
            return {};
        }
    }
}
